import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Grid, Typography } from "@material-ui/core";
import { KeyboardArrowLeft as ArrowLeft } from "@material-ui/icons";
import SectionContentWrapper from "../../../app/components/SectionContentWrapper";
import { translate as t } from "../../translate/localization";
import createBenefitPlan from "../../../usecases/benefit-plan/create-benefit-plan";
import useStyles from "./styles";
import CobeeTextField from "../../../app/components/CobeeTextField";
import SearchField from "../../../app/components/SearchField";
import TooltipIcon from "../../../app/components/TooltipIcon";
import MultipleSelect from "../../../app/components/MultipleSelect";
import { filterCsvEmployees } from "../../../usecases/employees/filter-csv-employees";
import FilterCsvButton from "../../../app/components/filterCsvButton/FilterCsvButton";
import { hideTipNotification } from "../../../usecases/commons/hide-tip-notification";
import CobeeCheckbox from "../../../app/components/CobeeCheckbox";
import Table from "../../../app/components/Table";
import filterAvailableEmployees from "../../../usecases/benefit-plan/filter-available-employees";
import modifyBenefitPlanEmployees from "../../../usecases/benefit-plan/modify-benefit-plan-employees";
import CobeeButton from "../../../app/components/CobeeButton";
import BenefitPlan from "../../../app/domain/BenefitPlan";
import addEmployeesToBenefitPlan from "../../../usecases/benefit-plan/add-employees-to-benefit-plan";
import renameBenefitPlan from "../../../usecases/benefit-plan/rename-benefit-plan";
import IconButton from "../../../app/components/IconButton";
import { displayBenefitPage } from "../../../usecases/benefit-model/displays/display-benefit-page";

const columns = [
  { id: "checkbox", label: "" },
  { id: "internalId", label: t("common-messages.internal-id") },
  { id: "nameAndSurname", label: t("common-messages.name-and-surname") },
  {
    id: "legalId",
    label: t("common-messages.nif-nie"),
  },
  {
    id: "email",
    label: t("common-messages.email"),
  },
  {
    id: "state",
    label: t("employees-section.employee-state"),
    align: "center",
  },
];

export default function BenefitPlanPage({
  benefitPlan,
  employees,
  totalEmployees,
  isLoading,
  availableFilters,
  currentFilters,
  searchValue,
  csvFilter,
  editMode,
}) {
  const {
    inputLabel,
    interactionBlockContainer,
    tooltipLabel,
    tooltipRow,
    filterContainer,
    selectAllContainer,
    benefitModelName,
    buttonBarContainer,
    titleContainer,
  } = useStyles();

  const [name, setName] = useState(benefitPlan?.name || "");
  const [csvStatus, setCsvStatus] = useState("info");

  const allEmployeesAreChecked = employees.reduce(
    (acc, { checked }) => acc && checked,
    true
  );

  const benefitPlanHasEmployees = benefitPlan?.employees.length > 0;

  useEffect(() => {
    setName(benefitPlan.name);
  }, [benefitPlan]);

  const handleLoadFile = async (e) => {
    const filename = e.target.files[0];
    const { isInvalid, result } = await filterCsvEmployees({ filename });
    if (isInvalid) {
      setCsvStatus("error");
    }
    filterAvailableEmployees({
      benefitPlan,
      availableFilters,
      totalEmployees,
      currentFilters,
      searchValue,
      csvFilter: result,
      editMode,
    });
  };

  const clearFileFilter = () => {
    hideTipNotification("notification-file-filter");
    setCsvStatus("info");
    filterAvailableEmployees({
      benefitPlan,
      availableFilters,
      totalEmployees,
      currentFilters,
      searchValue,
      csvFilter: [],
      editMode,
    });
  };

  const handleSelectAll = () => {
    modifyBenefitPlanEmployees({
      benefitPlan,
      availableFilters,
      currentFilters,
      employees,
      totalEmployees,
      whichEmployees: "all",
      value: !allEmployeesAreChecked,
      csvFilter,
      editMode,
    });
  };

  const handleCheckbox = ({ event, id }) => {
    modifyBenefitPlanEmployees({
      benefitPlan,
      availableFilters,
      currentFilters,
      employees,
      totalEmployees,
      whichEmployees: id,
      value: event.target.checked,
      csvFilter,
      editMode,
    });
  };

  const nameHasOnlySpaces = () => name.trim().length === 0;

  return (
    <main className="section">
      <Grid item>
        <IconButton icon={<ArrowLeft />} onClick={() => displayBenefitPage()}>
          {t("benefits-section.benefits")}
        </IconButton>
      </Grid>
      <Grid item xs={12}>
        <SectionContentWrapper
          title={
            <Grid item xs={12} container className={titleContainer}>
              <Typography variant="h4">
                {t(
                  `benefits-section.${
                    editMode ? "edit-benefit-plan" : "add-benefit-plan"
                  }`
                )}
              </Typography>
              {editMode && (
                <Typography variant="h4" className={benefitModelName}>
                  {`"${benefitPlan?.name}"`}
                </Typography>
              )}
            </Grid>
          }
        >
          <Grid item xs={12}>
            <Typography variant="body1" className={inputLabel}>
              {t("benefits-section.input-a-benefit-plan-name")}
            </Typography>
          </Grid>
          <Grid item xs={12} className={interactionBlockContainer}>
            <CobeeTextField
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
              value={name}
            />
          </Grid>
          {benefitPlan.name === "" || !benefitPlan.name || editMode ? (
            <Grid
              item
              xs={12}
              container
              justify="flex-end"
              className={
                benefitPlan.name !== "" ? interactionBlockContainer : ""
              }
            >
              {editMode ? (
                <CobeeButton
                  styleType="primary"
                  disabled={name.length < 1 || isLoading || nameHasOnlySpaces()}
                  isLoading={isLoading}
                  onClick={() => renameBenefitPlan({ name, benefitPlan })}
                >
                  {t("actions.save")}
                </CobeeButton>
              ) : (
                <CobeeButton
                  styleType="primary"
                  disabled={name.length < 1 || isLoading || nameHasOnlySpaces()}
                  isLoading={isLoading}
                  onClick={() => createBenefitPlan({ name })}
                >
                  {t("actions.continue")}
                </CobeeButton>
              )}
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography variant="body1">
                  {t("benefits-section.your-benefit-plan-applies-message")}
                </Typography>
              </Grid>
              <Grid item xs={12} className={tooltipRow}>
                <TooltipIcon />
                <Typography variant="caption" className={tooltipLabel}>
                  {t("benefits-section.you-may-select-employees-message")}
                </Typography>
              </Grid>
              <Grid item xs={12} className={interactionBlockContainer}>
                <SearchField
                  action={(value) => {
                    filterAvailableEmployees({
                      benefitPlan,
                      availableFilters,
                      totalEmployees,
                      currentFilters,
                      searchValue: value,
                      csvFilter,
                      editMode,
                    });
                  }}
                  isLoading={isLoading}
                />
              </Grid>
              <Grid item xs={12} container direction="row">
                {availableFilters &&
                  Object.keys(availableFilters)
                    .filter((key) => availableFilters[key].options.length > 1)
                    .map((key) => (
                      <Grid item className={filterContainer} key={key}>
                        <MultipleSelect
                          list={availableFilters[key].options.map(
                            ({ id, ...item }) => ({
                              value: id,
                              ...item,
                            })
                          )}
                          initValues={currentFilters[key]}
                          onChange={(e) =>
                            filterAvailableEmployees({
                              benefitPlan,
                              availableFilters,
                              totalEmployees,
                              searchValue,
                              currentFilters: {
                                ...currentFilters,
                                [key]: e.target.value,
                              },
                              csvFilter,
                              editMode,
                            })
                          }
                          placeholder={t(availableFilters[key].translationKey)}
                          disabled={isLoading}
                        />
                      </Grid>
                    ))}

                <Grid
                  item
                  className={`filter-section-container ${filterContainer}`}
                >
                  <div className="filter-item">
                    <FilterCsvButton
                      status={csvStatus}
                      uploadFile={handleLoadFile}
                      clearFilter={clearFileFilter}
                    />
                  </div>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                container
                alignItems="center"
                className={selectAllContainer}
              >
                <CobeeCheckbox
                  checked={
                    employees.reduce(
                      (acc, { checked }) => acc && checked,
                      true
                    ) || benefitPlan.employees.length === totalEmployees
                  }
                  onChange={handleSelectAll}
                />
                <Typography variant="body1" onClick={handleSelectAll}>
                  {t("common-messages.select-all", {
                    num: employees.length || 0,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12} className={interactionBlockContainer}>
                <Table
                  textEllipsisLimit={40}
                  columns={columns}
                  data={employees.map(
                    ({
                      name: employeeName,
                      surname,
                      state,
                      checked,
                      id,
                      ...rest
                    }) => ({
                      checkbox: (
                        <CobeeCheckbox
                          checked={
                            checked ||
                            benefitPlan.employees.find(
                              ({ id: employeeId }) => id === employeeId
                            )?.checked
                          }
                          onChange={(event) => handleCheckbox({ event, id })}
                        />
                      ),
                      nameAndSurname: `${employeeName} ${surname}`,
                      state: t(
                        `employees-section.${
                          state === "not-active" ? "disabled" : "enabled"
                        }`
                      ),
                      ...rest,
                    })
                  )}
                />
              </Grid>
              <Grid item xs={12} container justify="space-between">
                <Typography variant="body1">
                  {t(
                    "benefits-section.this-benefit-plan-applies-to-x-employees",
                    {
                      num: benefitPlan.employees.length,
                    }
                  )}
                </Typography>
                <div className={buttonBarContainer}>
                  <CobeeButton
                    styleType="secondary"
                    disabled={
                      name.length < 1 || isLoading || nameHasOnlySpaces()
                    }
                    isLoading={isLoading}
                    onClick={() => displayBenefitPage()}
                  >
                    {t("benefits-section.save-without-employees")}
                  </CobeeButton>
                  <CobeeButton
                    styleType="primary"
                    isLoading={isLoading}
                    disabled={
                      isLoading ||
                      !benefitPlanHasEmployees ||
                      nameHasOnlySpaces()
                    }
                    onClick={() =>
                      addEmployeesToBenefitPlan({ benefitPlan, employees })
                    }
                  >
                    {t("actions.save")}
                  </CobeeButton>
                </div>
              </Grid>
            </>
          )}
        </SectionContentWrapper>
      </Grid>
    </main>
  );
}

BenefitPlanPage.defaultProps = {
  benefitPlan: new BenefitPlan({}),
  isLoading: false,
  employees: [],
  currentFilters: {},
  searchValue: "",
  csvFilter: [],
  editMode: false,
  totalEmployees: undefined,
  availableFilters: {},
};

BenefitPlanPage.propTypes = {
  benefitPlan: PropTypes.object,
  isLoading: PropTypes.bool,
  employees: PropTypes.array,
  totalEmployees: PropTypes.number,
  availableFilters: PropTypes.object,
  currentFilters: PropTypes.object,
  searchValue: PropTypes.string,
  csvFilter: PropTypes.arrayOf(PropTypes.string),
  editMode: PropTypes.bool,
};
